import React, { useCallback, useMemo, useState } from 'react';
import { Dropdown, DropdownDivider, DropdownItem } from '../dropdown';
import { dueDateItems, menuItems } from '../../helpers/list';
import AssignPopupV2 from '../dropdowns/assignPopup';
import { useViewFilterAssignActions } from '../dropdowns/assignPopup/assignActions';
import { ICON_SIZE, statusItems } from '../../global/constants';
import { rootStore } from '../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import { isEmpty } from '../../helpers/common';
import Calendar from 'react-calendar';
import SVGIcon from '../../assets/images/svg/SVGIcon';
import { Calender } from './styles';
import { VIEWS_FILTER_TYPE } from '../../mobx/interfaces/view';
import dayjs from 'dayjs';
import { CustomerDetailsInterface } from '../../interfaces/CustomerInterface';
import { useMobileDevice } from '../../global/useMobile';
interface FilterDropdownsProps {
  onChangeStatus: (type: number) => void;
  onChangePriority: (type: number) => void;
  onChangeProject: (id: string) => void;
  onChangeDueDate: (date: string) => void;
  onChangeCustomer: (customer: CustomerDetailsInterface) => void;
  closeDropdown?: () => void;
}
const FilterDropdowns: React.FC<FilterDropdownsProps> = (props: FilterDropdownsProps) => {
  const { onChangeStatus, onChangePriority, onChangeProject, onChangeDueDate, onChangeCustomer, closeDropdown } = props;
  const { viewStore, workspaceStore, projectStore, customerStore } = rootStore;
  const { createViewInput } = viewStore;
  const { currentWorkspace } = workspaceStore;
  const { projects } = projectStore;
  const { customers } = customerStore;
  const isMobile = useMobileDevice();
  const assignActions = useViewFilterAssignActions();

  //State variables
  const [searchProject, setSearchProject] = useState(projects);
  const [searchCustomer, setSearchCustomer] = useState(customers);

  const handleProjectSearch = useCallback(
    (value: string) => {
      if (isEmpty(value)) return projects;
      const searchNewText = value.trim().toLowerCase();
      const words = searchNewText.split(' ').filter((word) => word.length > 0);
      const updatedList = projects?.filter((item) => {
        const title = item?.name.trim().toLowerCase() || '';
        return words.some((word) => title.includes(word));
      });
      setSearchProject(updatedList);
    },
    [projects]
  );

  const handleCustomerSearch = useCallback(
    (value: string) => {
      if (isEmpty(value)) return customers;
      const searchNewText = value.trim().toLowerCase();
      const words = searchNewText.split(' ').filter((word) => word.length > 0);
      const updatedList = customers?.filter((item: CustomerDetailsInterface) => {
        const title = item?.companyName?.trim().toLowerCase() || '';
        return words.some((word) => title.includes(word));
      });
      setSearchCustomer(updatedList);
    },
    [customers]
  );

  const onChangeDueRange = useCallback(
    (v: Date | [Date | null, Date | null]) => {
      const range: [Date | null, Date | null] = Array.isArray(v) ? v : [v, v];
      if (range[0] && range[1]) {
        // Convert to local YYYY-MM-DD format
        const start = dayjs(range[0]).startOf('day').format('YYYY-MM-DD');
        const end = dayjs(range[1]).endOf('day').format('YYYY-MM-DD');
        createViewInput.setDueDateFilter([start, end], VIEWS_FILTER_TYPE.RANGE);
        // Close dropdown after date range selection
        closeDropdown?.();
      }
    },
    [createViewInput, closeDropdown]
  );

  const formattedDates: [Date | null, Date | null] = useMemo(() => {
    return [
      createViewInput?.filters.dueDate.startDate ? new Date(createViewInput?.filters.dueDate.startDate) : null,
      createViewInput?.filters.dueDate.endDate ? new Date(createViewInput?.filters.dueDate.endDate) : null
    ];
  }, [createViewInput?.filters.dueDate.startDate, createViewInput?.filters.dueDate.endDate]);

  return (
    <>
      <Dropdown
        content={<DropdownItem label='Status' iconName='task-todo-icon' iconSize={16} iconTone='fill' />}
        trigger={isMobile ? 'click' : 'hover'}
        modalTitle='Status'
        isMinWidth={150}>
        {statusItems.map((item) => {
          const isChecked = createViewInput?.filters.status?.statuses.includes(item.type);
          return (
            <DropdownItem
              key={item.type}
              label={item.label}
              onClick={() => onChangeStatus(item.type)}
              iconName={item.iconName}
              isCheckBox={true}
              isChecked={isChecked}
            />
          );
        })}
      </Dropdown>

      <Dropdown
        content={<DropdownItem label='Priority' iconName='Priority-High-icon' iconSize={16} />}
        trigger={isMobile ? 'click' : 'hover'}
        modalTitle='Priority'
        isMinWidth={150}>
        {menuItems.map((item, index) => {
          const isChecked = createViewInput?.filters?.priority?.priorities.includes(item.key);
          return (
            <DropdownItem
              key={index}
              label={item.label}
              onClick={() => onChangePriority(item.key)}
              iconName={item.icon}
              isCheckBox={true}
              isChecked={isChecked}
            />
          );
        })}
      </Dropdown>
      <AssignPopupV2
        projectUsers={currentWorkspace?.users || []}
        context={{ mode: 'viewFilter' }}
        onAssign={assignActions.onAssign}
        onUnassign={assignActions.onUnassign}
        isContextMenu={true}
        isMultiAssignee={true}
      />

      <Dropdown
        content={<DropdownItem label='Due date' iconName='calendar-icon' iconSize={16} iconViewBox='0 0 18 18' />}
        trigger={isMobile ? 'click' : 'hover'}
        modalTitle='Due date'
        isMinWidth={250}
        isRemoveMaxHeight>
        {dueDateItems.map((item, index) => (
          <DropdownItem key={index} label={item.label} onClick={() => onChangeDueDate(item.label)} />
        ))}
        <DropdownDivider />
        <Calender role='button' onClick={(e) => e.stopPropagation()}>
          <Calendar
            selectRange={true}
            value={formattedDates}
            onChange={onChangeDueRange}
            prev2Label={
              <SVGIcon
                name='calender-prev2Label'
                width={ICON_SIZE.SM}
                height={ICON_SIZE.SM}
                viewBox={`0 0 ${ICON_SIZE.MD} ${ICON_SIZE.MD}`}
              />
            }
            prevLabel={
              <SVGIcon
                name='calender-prevLabel'
                width={ICON_SIZE.SM}
                height={ICON_SIZE.SM}
                viewBox={`0 0 ${ICON_SIZE.MD} ${ICON_SIZE.MD}`}
              />
            }
            next2Label={
              <SVGIcon
                name='calender-next2Label'
                width={ICON_SIZE.SM}
                height={ICON_SIZE.SM}
                viewBox={`0 0 ${ICON_SIZE.MD} ${ICON_SIZE.MD}`}
              />
            }
            nextLabel={
              <SVGIcon
                name='calender-nextLabel'
                width={ICON_SIZE.SM}
                height={ICON_SIZE.SM}
                viewBox={`0 0 ${ICON_SIZE.MD} ${ICON_SIZE.MD}`}
              />
            }
          />
        </Calender>
      </Dropdown>

      <Dropdown
        content={<DropdownItem label='Project' iconName='project-icon' iconSize={16} iconViewBox='0 0 18 18' />}
        trigger={isMobile ? 'click' : 'hover'}
        modalTitle='Project'
        isMinWidth={150}
        onChangeSearch={handleProjectSearch}>
        {searchProject?.map((item, index) => {
          const isChecked = createViewInput?.filters?.project?.projects.includes(item.id);
          return (
            <DropdownItem
              key={index}
              label={item.name}
              isCheckBox={true}
              isChecked={isChecked}
              onClick={() => onChangeProject(item.id)}
            />
          );
        })}
      </Dropdown>
      {!isEmpty(searchCustomer) && (
        <Dropdown
          content={<DropdownItem label='Customer' iconName='customer-icon' iconSize={16} iconViewBox='0 0 18 18' />}
          trigger={isMobile ? 'click' : 'hover'}
          modalTitle='Customer'
          isMinWidth={150}
          onChangeSearch={handleCustomerSearch}>
          {searchCustomer?.map((item: CustomerDetailsInterface, index: number) => {
            const isChecked = createViewInput?.filters?.customer?.customers.includes(item.id);
            return (
              <DropdownItem
                key={index}
                label={item.companyName}
                isCheckBox={true}
                isChecked={isChecked}
                onClick={() => onChangeCustomer(item)}
                avatarSrc={item.profileImage ? item.profileImage : 'empty_profile'}
                isAvatar={true}
              />
            );
          })}
        </Dropdown>
      )}
    </>
  );
};

export default observer(FilterDropdowns);
